// Feature: MCRO001-F0021 (bounded slice) + MCRO001-F0039 — "On this canvas": the
// media attached to this routine's canvas, read from the choreo backend's
// choreography_routine_media_links table. Sits beside MediaLibraryGrid ("Your Video
// Library", org-wide) as the drop DESTINATION the owner asked for: "the video
// library is everything I have access to... the canvas list is what is shown on the
// canvas."
//
// STRUCK (D021 supersedes, 2026-09-03): the "org-wide" gloss and the quoted
// owner statement above ("the video library is everything I have access to")
// are the pre-F0159 rationale. MCRO001-D021 §1.1 makes "Your Video Library"
// PERSONAL — it renders only the viewer's own uploads, not everything the
// viewer has access to org-wide. The drop-DESTINATION role this paragraph
// describes (this pane as where a drag lands) is unchanged; only the library's
// scope claim is superseded.
//
// Drop target: accepts MediaLibraryGrid's DRAG_MIME payload (the same
// {orgId, mediaIds} shape DemonstrationWorkspace already reads for M004-F0392) and
// calls onAttach for every dragged id — optimistic add with rollback handled by the
// caller (MusicChoreography.js), not here. Distinguishes an incoming OS file drag
// (Files, no DRAG_MIME) from an internal attach-drag exactly like MediaLibraryGrid's
// own isIncomingFileDrag does, so this panel never accidentally swallows an upload.
//
// ---------------------------------------------------------------------------
// MCRO001-F0039 / T-MCRO-039 (2026-07-27): ✕ MEANS "REMOVE FROM CANVAS". ALWAYS.
// ---------------------------------------------------------------------------
// This panel used to give the same ✕ glyph, in the same position, two different
// meanings decided by the tile's `origin`:
//
//   - `origin: 'linked'` → detach the link row. Correct.
//   - `origin: 'owned'`  → choreographyApi.archiveMedia(), which removed the video
//                          from the WHOLE ORGANISATION'S Video Library.
//
// The second was reasoned from a real constraint — owned media had no
// choreography_routine_media_links row, so there was genuinely nothing to detach —
// but the conclusion was wrong. `choreography_media.routine_id` is single-valued, so
// a video uploaded straight into a routine is ALWAYS `owned`; in production the link
// table was completely empty, which made every ✕ on this pane a library delete. On
// 2026-07-26 the owner removed a tile and lost the video. It was recoverable only by
// clearing `archived_at` directly in the database.
//
// The fix is in the data model, not here: every media row now has an own-routine link
// row (choreo migration 108 backfills them; every media-creating path writes one), so
// detach always has something to remove and removing the last link leaves the media
// in the library. This component's job is simply to stop making the distinction:
// ONE control, ONE meaning, one accessible name, for every tile.
//
// `origin` is still rendered — the ⛓ badge and the `data-origin` attribute stay,
// because where a video came from is still true and MCRO001-F0021 still depends on
// it. Only the ACTION stopped depending on it.
//
// The destructive delete now lives ONLY on a Library tile (MediaLibraryGrid), behind
// a confirmation naming the consequence and the retention period, and it archives
// into a recoverable Trash rather than destroying anything
// (MCRO001-F0040 / T-MCRO-040). This component no longer imports choreographyApi at
// all: it cannot delete, by construction.

import React, { useCallback, useEffect, useState } from 'react';
import { DRAG_MIME, defaultMintStreamUrl, mediaIsImage, sensibleTitle, useStreamUrl } from './MediaLibraryGrid';

function tileLabel(item) {
  return sensibleTitle(item, 'Untitled video');
}

// Prefer the backend's actual message (errorHandler.ts always sends
// { success:false, error: CODE, message: '...human readable...' }) over Axios's
// own generic "Request failed with status code NNN" — the previous code read
// error.message directly, which silently discarded the real reason for a failure.
function apiErrorMessage(error, fallback) {
  return error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback;
}

// MCRO001-F0039 / T-MCRO-039 Case B: ONE label for every tile. The `is-destructive`
// modifier is gone from this component entirely — nothing this panel does is
// destructive, so nothing here may look destructive.
const REMOVE_LABEL = 'Remove from canvas';

// Issue #22 (MCRO001-F0021 / T-MCRO-021): a canvas tile shows the SAME real
// poster the Library tile shows for this exact media — minted through the
// identical cache + bulk-mint queue + mint call MediaTile uses
// (MediaLibraryGrid.js's `useStreamUrl`/`defaultMintStreamUrl`, imported rather
// than reimplemented, so this can never become a second, divergent poster
// path). Video tiles mint the `poster` variant; image tiles mint their single
// `content` artifact directly — the exact same variant rule as MediaTile
// (T-MCRO-032 case F/H). `bulk: true` puts canvas-tile mints under the SAME
// concurrency cap library tiles mint under (both panes share one orgId on one
// page), so neither pane's mints can starve the other's.
//
// A poster that is not ready yet, is absent (pre-F0032 media), or fails to
// load degrades to the pre-existing placeholder glyph — it must NEVER hide or
// break the tile (mirrors the #20 poster-missing regression guard; see the
// "keeps the glyph" tests in CanvasMediaPanel.test.js).
function CanvasTile({ item, orgId, mintStreamUrl, onRemove, removing }) {
  const image = mediaIsImage(item);
  const linked = item?.origin === 'linked';
  const posterVariant = image ? 'content' : 'poster';
  const poster = useStreamUrl(mintStreamUrl, orgId, item, posterVariant, true, true);
  const [posterFailed, setPosterFailed] = useState(false);
  useEffect(() => { setPosterFailed(false); }, [poster.url]);
  const posterReady = poster.status === 'ready' && !posterFailed;
  return (
    <div className={`mc-canvas-tile ${linked ? 'is-linked' : ''}`} data-media-id={item?.mediaId} data-origin={item?.origin || 'owned'}>
      <div className="mc-canvas-tile-frame" aria-hidden="true">
        {posterReady
          ? <img className="mc-canvas-tile-image" src={poster.url} alt="" draggable={false} loading="lazy" decoding="async" onError={() => setPosterFailed(true)} />
          : <span className="mc-canvas-tile-placeholder">{image ? '▧' : '▶'}</span>}
        {/* Provenance survives (T-MCRO-039 Case E): the badge still says where a
            linked video came from. It just no longer changes what ✕ does. */}
        {linked && <span className="mc-canvas-tile-linked-badge" title="Attached from another routine">⛓</span>}
      </div>
      <button
        type="button"
        className="mc-canvas-tile-remove"
        aria-label={`${REMOVE_LABEL}: ${tileLabel(item)}`}
        title={REMOVE_LABEL}
        onClick={() => onRemove(item)}
        disabled={removing}
      >{removing ? '…' : '✕'}</button>
    </div>
  );
}

// `getContent` and `confirm` are still gone from the signature: `getContent`
// existed only to reach choreographyApi.archiveMedia (via the expanded
// player), `confirm` existed only to gate that archive call, and neither has
// any legitimate use left now that this panel has no delete path.
//
// `orgId` is back (issue #22 / MCRO001-F0021), but ONLY to mint each tile's
// poster stream URL — the same read-only `useStreamUrl`/`defaultMintStreamUrl`
// MediaLibraryGrid's own tiles use, imported rather than reimplemented. This
// does not reopen the delete hole T-MCRO-039 closed: this file still never
// imports choreographyApi directly, and `defaultMintStreamUrl` reaches only
// choreo's stream-url mint endpoint, never archiveMedia — grep this file for
// "archive" or "choreographyApi" to confirm neither exists here.
export default function CanvasMediaPanel({
  items = [],
  orgId,
  canEdit = false,
  onAttach,
  onDetach,
  // Mints a short-lived, variant-locked signed stream URL for a tile's poster:
  // (orgId, mediaId, variant) => Promise<{ data: { url, expiresAt } }>. Defaults
  // to the real choreo API call — the exact same default MediaLibraryGrid's own
  // tiles use — so MusicChoreography.js needs only to start passing `orgId`
  // (added alongside this prop) for production posters to work; tests inject a
  // mock override here exactly as MediaLibraryGrid.test.js does.
  mintStreamUrl = defaultMintStreamUrl,
}) {
  const [dragOverDepth, setDragOverDepth] = useState(0);
  const [pendingRemoval, setPendingRemoval] = useState(() => new Set());
  const [announcement, setAnnouncement] = useState('');
  const [errorNotice, setErrorNotice] = useState('');
  const canDrop = canEdit && typeof onAttach === 'function';
  // The local `archivedIds` optimistic-hide set is gone with the archive path.
  // `items` is the parent's canvas list and detachFromCanvas already removes
  // optimistically and rolls back, so this panel has nothing of its own to hide.
  const visibleItems = items;

  const isMediaDrag = (event) => {
    const types = event?.dataTransfer?.types;
    return Boolean(types) && Array.from(types).includes(DRAG_MIME);
  };

  const onDragOver = useCallback((event) => {
    if (!canDrop || !isMediaDrag(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, [canDrop]);

  const onDragEnter = useCallback((event) => {
    if (!canDrop || !isMediaDrag(event)) return;
    event.preventDefault();
    setDragOverDepth((depth) => depth + 1);
  }, [canDrop]);

  const onDragLeave = useCallback((event) => {
    if (!canDrop || !isMediaDrag(event)) return;
    event.preventDefault();
    setDragOverDepth((depth) => Math.max(0, depth - 1));
  }, [canDrop]);

  const onDrop = useCallback((event) => {
    if (!canDrop || !isMediaDrag(event)) return;
    event.preventDefault();
    setDragOverDepth(0);
    let dropped = null;
    try { dropped = JSON.parse(event.dataTransfer.getData(DRAG_MIME) || '{}'); } catch { dropped = null; }
    const mediaIds = Array.isArray(dropped?.mediaIds) ? dropped.mediaIds.filter(Boolean) : [];
    if (mediaIds.length === 0) return;
    onAttach(mediaIds);
    setAnnouncement(mediaIds.length === 1 ? 'Added 1 video to this canvas.' : `Added ${mediaIds.length} videos to this canvas.`);
  }, [canDrop, onAttach]);

  // MCRO001-F0039 / T-MCRO-039 Cases A, B, D, I. One handler, one meaning, for every
  // tile. It removes this routine's canvas link and nothing else: the video stays in
  // the Video Library, on every other canvas it is attached to, and re-attachable.
  // There is deliberately NO confirmation — removing a video from a canvas is not a
  // destructive act, and a confirmation here would train the user to click through
  // the one in the Library that is.
  const remove = useCallback(async (item) => {
    if (typeof onDetach !== 'function') return;
    const key = String(item?.mediaId || '');
    setPendingRemoval((prev) => new Set(prev).add(key));
    setErrorNotice('');
    try {
      await onDetach(item);
      setAnnouncement(`Removed ${tileLabel(item)} from this canvas. It is still in your video library.`);
    } catch (error) {
      // onDetach (MusicChoreography.js's detachFromCanvas) already rolls its own
      // optimistic list back to `previous` before re-throwing, so the tile
      // reappears via the `items` prop; this panel only needs to report why —
      // visibly, not only into the screen-reader-only live region (Case I).
      const message = apiErrorMessage(error, `Could not remove ${tileLabel(item)} from this canvas.`);
      setAnnouncement(message);
      setErrorNotice(message);
    } finally {
      setPendingRemoval((prev) => { const next = new Set(prev); next.delete(key); return next; });
    }
  }, [onDetach]);

  const showDropOverlay = canDrop && dragOverDepth > 0;

  return (
    <aside
      className={`mc-canvas-panel ${showDropOverlay ? 'is-drag-over' : ''}`}
      aria-label="On this canvas"
      onDragOver={onDragOver}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <header className="mc-canvas-panel-head">
        <span className="mc-kicker">On this canvas</span>
        <h2>Canvas</h2>
      </header>

      {/* aria-live alone (no role="status") announces attach/detach without
          competing with getByRole('status') queries elsewhere on the page — an
          explicit role="status" is redundant with aria-live="polite" (it already
          implies that live-region politeness) and would otherwise collide with the
          page's single visible "status" notice region. */}
      <div className="mc-visually-hidden" aria-live="polite">{announcement}</div>

      {/* Visible, non-hidden failure surface — a failed remove/archive must never
          look like nothing happened. Reuses the shared .mc-alert treatment
          (MusicChoreography.css) rather than the page's single `canvasNotice`
          status region (attach/detach elsewhere), so this panel's own errors are
          never silently overwritten by an unrelated notice, and vice versa. */}
      {errorNotice && <div className="mc-alert" role="alert">{errorNotice}</div>}

      {showDropOverlay && (
        <div className="mc-canvas-panel-dropzone-overlay" role="presentation">
          <div className="mc-canvas-panel-dropzone-message"><span aria-hidden="true">⇩</span> Drop to add to this canvas</div>
        </div>
      )}

      {visibleItems.length === 0 ? (
        <div className="mc-canvas-panel-empty">
          <div className="mc-canvas-panel-empty-glyph" aria-hidden="true">▶</div>
          <p>Nothing on this canvas yet — drag videos here from your library.</p>
        </div>
      ) : (
        <div className="mc-canvas-panel-grid" role="list">
          {visibleItems.map((item) => (
            <div role="listitem" key={item.mediaId}>
              <CanvasTile
                item={item}
                orgId={orgId}
                mintStreamUrl={mintStreamUrl}
                onRemove={remove}
                removing={pendingRemoval.has(String(item.mediaId))}
              />
            </div>
          ))}
        </div>
      )}
    </aside>
  );
}
